import { translate } from '../utils/translator';
import { getPrice } from '../utils/translator'
import { clientConfig } from '../config'
import { useCartContext } from '../customHooks/useCart'
import { useParams, useSearchParams } from 'react-router-dom'
import QuantityControl from './QuantityControl/QuantityControl'
import { useShippingPageContext } from '../pages/v2/ShippingV2/providers/ShippingPageProvider'
import { useMemo } from 'react'
import { displayedMedicationPrice } from '../utils/helpers'

type Props = {
  cartViewProduct: CartProductView
  className?: string
  showQuantityControl?: boolean
  wasUpgradedToPremium?: boolean
  isCannabisCatalog?: boolean
}
const CANNABIS_ID_TITLE = "Digitaler Cannabis-Ausweis";

const CartViewProductRow: React.FC<Props> = ({ cartViewProduct, className, showQuantityControl = false, wasUpgradedToPremium, isCannabisCatalog = false }) => {

  const prescriptionOnlyImage = clientConfig.IMAGES_URL + clientConfig.PRESCRIPTION_IMAGE;
  const { getCart, cartCurrency } = useCartContext();
  const { handleOTCProductDecrement, handleOTCProductIncrement  } = useShippingPageContext();
  const { country } = useParams();
  const cart = getCart();
  const isRiskGroupBlocked = Boolean(cartViewProduct.risk_group_id && cart.risk_groups?.some((group) => group.id === cartViewProduct.risk_group_id && group.status !== 'clear'));
  const numberOfProductItemUnits = cartViewProduct.numberOfUnits;
  const isPrescriptionOnlyProduct = cart.only_prescription && cartViewProduct.type === "prescription_drug"
  // For a Care+ plan order the service fee is already covered by the Care+ plan price, so the
  // medication row must show the pure official price (not official + service fee).
  const isCarePlusPlanOrder = Number(cart.care_plus_plan_fee ?? 0) > 0;
  // Care+ keeps today's official-only row; catalog setting only applies to regular RX.
  const displayedPriceComponents = isCarePlusPlanOrder ? null : (cart.displayed_price_components ?? null);
  const basePrice = isCarePlusPlanOrder
    ? cartViewProduct.official_price
    : displayedPriceComponents
      ? displayedMedicationPrice(cartViewProduct, displayedPriceComponents)
      : cartViewProduct.official_price + (cartViewProduct.service_fee || 0);
  const pricePerGram = (isCannabisCatalog ?  cartViewProduct.official_price: basePrice) / (numberOfProductItemUnits|| 1) ;
  const isCannabisProduct = cartViewProduct.type === "cannabis_id" || cartViewProduct.title === CANNABIS_ID_TITLE
  const [searchParams] = useSearchParams();
  const { language } = useParams()
  const rowIdPrefix = `cart-view-product-row-${String(cartViewProduct.product_item_id ?? cartViewProduct.title).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  const getRowId = (suffix: string) => `${rowIdPrefix}-${suffix}`;


  const CartViewProductAsProductItem = useMemo(() => {
    return {
      id: cartViewProduct.product_item_id,
      title: cartViewProduct.title,
      quantity: String(cartViewProduct.quantity),
      price: cartViewProduct.item_price,
      official_price: cartViewProduct.official_price,
      basePrice: cartViewProduct.official_price + (cartViewProduct.service_fee || 0),
      type: cartViewProduct.type,
      description: "", //TODO:
      custom_quantity_title: cartViewProduct.quantityStr,
      imageSrc: cartViewProduct.imageSrc
    } as ProductItem
  }, [cart.products, cartViewProduct])


  const productPriceRow = useMemo(() => {
    let price = 0;

    if (cartViewProduct.type === "video_consultation") {
      return <></>
    }

    if (cartViewProduct.is_prescription_drug) {
      // Cannaleo flow or only prescription mode - No price should be shown
      if ((searchParams.get("epd") && !wasUpgradedToPremium) || cart.only_prescription) {
        price = 0;
      } else if (country?.toUpperCase() === "DE") {
        // Regular flow (not prescription-only) - Show price in DE
        price = cartViewProduct.official_price * cartViewProduct.quantity!
      } else {
        // Regular flow (not prescription-only) - Show price
        price = basePrice * cartViewProduct.quantity!
      }
    }

    // If the product is NOT a prescription drug
    else {
      if ((cartViewProduct.type === "cannabis_id" || cartViewProduct.title === CANNABIS_ID_TITLE) && !cart.only_prescription) { //Cannabis card product
        price = 0;
      }
       else if (country?.toUpperCase() === "DE") {
        // Show price for regular OTC products in DE
        price = cartViewProduct.official_price * cartViewProduct.quantity!
      } else {
        // Show price for regular OTC products
        price = basePrice * cartViewProduct.quantity!
      }
    }

    if (cartCurrency?.locale === "de-DE" || cartCurrency?.locale === "de-CH" || cartCurrency?.locale === "ch-CH" || cartCurrency?.locale === "fr-CH") {
      return <></>
    }

    return (
      <p id={getRowId('price')} className='font-roboto font-medium text-md text-[#0D2C54]' translate="no">
        {getPrice(price, cartCurrency.locale, cartCurrency.rate)}
      </p>
    );
  }, [cart.only_prescription, cartViewProduct.item_price, cartViewProduct.quantity, cartCurrency, searchParams, wasUpgradedToPremium]);


  return (
    <div id={getRowId('container')} className={` flex flex-col justify-start gap-2 pr-3 ${className}`}>
      <div id={getRowId('upper-row')} className='flex flex-row justify-between gap-2'>

          {/* Image */}
            <img src={(cart.only_prescription && cartViewProduct.is_prescription_drug) ? prescriptionOnlyImage : cartViewProduct.imageSrc} alt="Product" style={{ width: "60px", height: "60px", objectFit: "cover" }} />

          {/* Title & Quantity */}
          <div id={getRowId('details')} className='flex items-start flex-col min-w-[80%]'>
            <div className='flex flex-row justify-between w-full gap-0.5'>
            <p id={getRowId('title')} className='font-roboto font-medium text-sm text-[#0D2C54] break-words !leading-[15px] max-w-[60%] md:max-w-[155px]'> {cartViewProduct.title} </p>

              {/* Price row */}
              {((searchParams.get("epd") && !wasUpgradedToPremium) || cart.only_prescription) && cartViewProduct.is_prescription_drug ? null : productPriceRow}

              {/* Price display and calculation based on product type */}
              {(!isCannabisCatalog )&& ['DE', 'CH'].includes(country?.toUpperCase() || "") && !isPrescriptionOnlyProduct ? (
                <>
                  {/* Price for OTC products - moved to right side */}
                    <p id={getRowId('price-otc')} className='text-base font-roboto font-medium text-[#0D2C54] whitespace-nowrap' translate="no">
                      {
                        isCannabisProduct
                      ? 
                      (getPrice(cart.only_prescription ? (cartViewProduct?.item_price ? cartViewProduct.item_price : 0) : 0, cartCurrency.locale, cartCurrency.rate))
                      : 
                      getPrice(basePrice, cartCurrency.locale, cartCurrency.rate)
                      }
                    </p>
                </>
              ) : cartViewProduct.type === "prescription_drug" && isCannabisCatalog && country?.toUpperCase() === "DE" && !isPrescriptionOnlyProduct ? (
                <div className='flex flex-col items-end'>
                  {/* Price per gram for prescription drugs - moved to right side */}
                    <p id={getRowId('price-per-gram')} className='text-base font-roboto font-medium text-[#0D2C54] whitespace-nowrap' translate="no">
                      {getPrice(numberOfProductItemUnits ? (pricePerGram) : 0, cartCurrency.locale, cartCurrency.rate)} /{cartViewProduct?.quantityStr?.split(' ')?.[1] == 'Gramm' ? 'g' : cartViewProduct?.quantityStr?.split(' ')?.[1]}
                    </p>
                </div>
                  
              ) : null}
            </div>
            
            {!["cannabis_id", "schengen_id", "video_consultation", "test_kit"].includes(cartViewProduct.type!) || cartViewProduct.title === CANNABIS_ID_TITLE ?
              <div className='flex flex-row justify-between w-full'>
                <div className='mt-1'>
                  {cartViewProduct.title !== CANNABIS_ID_TITLE && (
                    <p id={getRowId('dosage')} className='text-[#9D9D9D] text-xs font-roboto'>
                      {translate(language, "shipping", "otcCartPopup/package")}: {cartViewProduct.quantityStr}
                    </p>
                  )}
                  <p id={getRowId('quantity')} className='text-[#9D9D9D] text-xs font-roboto'> {translate(language, "default", "OrderWindow/quantity")}: {cartViewProduct.quantity} </p>
                </div>

                  {/* Price calculation for OTC products */}
                  { (!isCannabisCatalog )&& ['DE', 'CH'].includes(country?.toUpperCase() || "") && !isPrescriptionOnlyProduct ? ( 
                    <>
                      <p id={getRowId('price-calculation-otc')} className='text-xs text-[#9D9D9D] whitespace-nowrap self-end font-roboto' translate="no">
                      {
                        isCannabisProduct ? "" : (getPrice(basePrice, cartCurrency.locale, cartCurrency.rate) + " × " + cartViewProduct.quantity + " = " + getPrice(basePrice * cartViewProduct.quantity, cartCurrency.locale, cartCurrency.rate))
                      }
                      </p>
                    </>
                  ) :  null}

              </div>
              :
              null
            } 
          </div>

      </div>


      {
        Boolean(showQuantityControl) && (!cartViewProduct.is_test_lab || cart.allow_multiple_test_lab_kits === true) &&
        <div id={getRowId('bottom-row')} className='w-full flex justify-end'>
          <QuantityControl
            idPrefix={getRowId('quantity-control')}
            decrementButtonOnClick={() => handleOTCProductDecrement(CartViewProductAsProductItem)}
            incrementButtonOnClick={() => handleOTCProductIncrement(CartViewProductAsProductItem)}
            isIncrementDisabled={isRiskGroupBlocked}
            quantity={cartViewProduct.quantity} />
        </div>
      }


    </div>
  )
}

export default CartViewProductRow

